import { motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Eye,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { createLogger } from "../../lib/logger";
import { fadeIn, fadeInUp, stagger } from "../../lib/motion";
import { notify } from "../../lib/notify";
import { useAppStore } from "../../store/useAppStore";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { PageHeader } from "../ui/page-header";

const log = createLogger("DeploymentsView");

export function DeploymentsView() {
  const { deployments, setDeployments, projects } = useAppStore();
  const [loading, setLoading] = useState(true);

  const fetchDeployments = useCallback(async () => {
    try {
      const data = (await api.deployments.list()) as any;
      setDeployments(data.deployments || []);
    } catch (error) {
      log.error("Failed to fetch deployments", error);
    } finally {
      setLoading(false);
    }
  }, [setDeployments]);

  const viewDeploymentLogs = async (deploymentId: string) => {
    try {
      const data = (await api.deployments.get(deploymentId)) as any;
      log.debug("Deployment logs", data.logs);
      notify.info("Deployment logs", {
        description: `Logs for deployment ${deploymentId}:\n${
          data.logs?.join("\n") || "No logs available"
        }`,
      });
    } catch (error) {
      log.error("Failed to fetch logs", error);
      notify.error("Something went wrong", {
        description: "Failed to fetch deployment logs",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "deployed":
      case "running":
        return <CheckCircle className="h-5 w-5 text-success" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-destructive" />;
      case "building":
      case "pending":
        return <Clock className="h-5 w-5 text-warning" />;
      default:
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  return (
    <div className="space-y-6">
      <motion.div
        className="flex items-center justify-between"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
      >
        <PageHeader
          eyebrow="Workspace"
          title="Deployments"
          description="Monitor your application deployments and their status"
          className="w-full"
        />
      </motion.div>

      {loading ? (
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="py-8 text-center"
        >
          <div className="text-muted-foreground">Loading deployments...</div>
        </motion.div>
      ) : deployments.length === 0 ? (
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="py-12 text-center"
        >
          <div className="mb-4 text-muted-foreground">No deployments yet</div>
          <p className="text-sm text-muted-foreground">
            Create a project and deploy your first application to see
            deployments here.
          </p>
        </motion.div>
      ) : (
        <motion.div
          className="grid gap-4"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          {deployments.map((deployment) => (
            <motion.div key={deployment.id} variants={fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(deployment.status)}
                      <span className="ml-2">
                        {deployment.name || getProjectName(deployment.projectId)}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(deployment.createdAt).toLocaleString()}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-muted-foreground">
                        {deployment.branch || "main"} branch
                      </div>
                      <Badge
                        variant={
                          deployment.status === "running"
                            ? "default"
                            : deployment.status === "failed"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {deployment.status === "running"
                          ? "deployed"
                          : deployment.status}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      {(deployment.url ||
                        deployment.customDomain ||
                        deployment.subdomain) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const url =
                              deployment.url ||
                              (deployment.customDomain
                                ? `https://${deployment.customDomain}`
                                : "") ||
                              (deployment.subdomain
                                ? `https://${deployment.subdomain}.avahana.dev`
                                : "");
                            if (url) window.open(url, "_blank");
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewDeploymentLogs(deployment.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
